import {
    ColumnType,
    MicrostoreElementState,
    OrganizationState,
    ProductState,
    SiteState,
    SiteType,
    TotemState,
    UserState,
    type ColumnOptionsScopeInput,
    type LocationOptionsScopeInput,
    type MicrostoreColumnGroupOptionsScopeInput,
    type MicrostoreColumnOptionsScopeInput,
    type OrganizationOptionsScopeInput,
    type ProductOptionsScopeInput,
    type SiteOptionsScopeInput,
    type SubcategoryOptionsScopeInput,
    type TotemOptionsScopeInput,
    type UserOptionsScopeInput,
} from 'data/__generated__';

import { defineEntitySource } from './types';

/**
 * Le registre des collections sélectionnables de la back-office.
 *
 * Une entrée = une collection, et c'est **tout ce qu'il y a à écrire côté front** : pas de query, pas
 * de `.map()`, pas de libellé. La query est construite depuis `field`, et le libellé vient de
 * l'`optionsConfig` du repository côté API.
 *
 * Ajouter une collection : déclarer son `optionsConfig` et son endpoint côté API, puis une entrée ici.
 * `sources.test.ts` vérifie que chaque entrée correspond bien au schéma.
 */

export const siteSource = defineEntitySource<SiteOptionsScopeInput>({
    key: 'site',
    labels: { singular: 'Site', plural: 'Sites' },
    field: 'siteOptions',
    scopeType: 'SiteOptionsScopeInput',
    // Un site archivé n'est pas PROPOSÉ — mais s'il est déjà sélectionné, il garde son nom : la
    // résolution par identifiants ignore le périmètre côté serveur.
    scope: { states: [SiteState.Regular], types: null },
});

/** Variante d'entrepôts. Constante de module : une source se compare par référence. */
export const warehouseSiteSource = siteSource.withScope({
    states: [SiteState.Regular],
    types: [SiteType.Warehouse],
});

export const organizationSource = defineEntitySource<OrganizationOptionsScopeInput>({
    key: 'organization',
    labels: { singular: 'Organisation', plural: 'Organisations' },
    field: 'organizationOptions',
    scopeType: 'OrganizationOptionsScopeInput',
    scope: { states: [OrganizationState.Rollout] },
});

/** Référentiels à recherche serveur : trop gros pour tenir en mémoire. */
export const userSource = defineEntitySource<UserOptionsScopeInput>({
    key: 'user',
    labels: { singular: 'Utilisateur', plural: 'Utilisateurs' },
    field: 'userOptions',
    scopeType: 'UserOptionsScopeInput',
    scope: { permissions: null, states: [UserState.Regular] },
});

export const productSource = defineEntitySource<ProductOptionsScopeInput>({
    key: 'product',
    labels: { singular: 'Produit', plural: 'Produits' },
    field: 'productOptions',
    scopeType: 'ProductOptionsScopeInput',
    scope: { states: [ProductState.Regular], isMicrostore: null, flows: null },
});

export const totemSource = defineEntitySource<TotemOptionsScopeInput>({
    key: 'totem',
    labels: { singular: 'Totem', plural: 'Totems' },
    field: 'totemOptions',
    scopeType: 'TotemOptionsScopeInput',
    scope: { states: [TotemState.Rollout], organizationIds: null },
});

export const locationSource = defineEntitySource<LocationOptionsScopeInput>({
    key: 'location',
    labels: { singular: 'Emplacement', plural: 'Emplacements' },
    field: 'locationOptions',
    scopeType: 'LocationOptionsScopeInput',
});

export const microstoreColumnGroupSource = defineEntitySource<MicrostoreColumnGroupOptionsScopeInput>({
    key: 'microstoreColumnGroup',
    labels: { singular: 'Groupe de colonnes', plural: 'Groupes de colonnes' },
    field: 'microstoreColumnGroupOptions',
    scopeType: 'MicrostoreColumnGroupOptionsScopeInput',
    scope: { states: [MicrostoreElementState.Regular], siteIds: null },
});

export const subcategorySource = defineEntitySource<SubcategoryOptionsScopeInput>({
    key: 'subcategory',
    labels: { singular: 'Sous-catégorie', plural: 'Sous-catégories' },
    field: 'subcategoryOptions',
    scopeType: 'SubcategoryOptionsScopeInput',
    preload: true,
});

export const columnSource = defineEntitySource<ColumnOptionsScopeInput>({
    key: 'column',
    labels: { singular: 'Colonne', plural: 'Colonnes' },
    field: 'columnOptions',
    scopeType: 'ColumnOptionsScopeInput',
    scope: { states: [MicrostoreElementState.Regular], types: null },
});

export const microstoreColumnSource = defineEntitySource<MicrostoreColumnOptionsScopeInput>({
    key: 'microstoreColumn',
    labels: { singular: 'Machine', plural: 'Machines' },
    field: 'microstoreColumnOptions',
    scopeType: 'MicrostoreColumnOptionsScopeInput',
    scope: { states: [MicrostoreElementState.Regular], siteIds: null },
});

/** Les colonnes d'entrepôt, périmètre le plus demandé. Constante de module : comparée par référence. */
export const warehouseColumnSource = columnSource.withScope({
    states: [MicrostoreElementState.Regular],
    types: [ColumnType.Warehouse, ColumnType.BrokenProductsWarehouse, ColumnType.ExpiredProductsWarehouse],
});

/**
 * Petits référentiels : aucun périmètre à faire varier, et le serveur les rend d'un coup
 * (`optionsConfig.preload`). Le front filtre alors localement, sans aller-retour par frappe — et si
 * l'un d'eux grossit, il repasse en recherche serveur sans qu'aucun appelant ne bouge.
 */
export const supplierSource = defineEntitySource({
    key: 'supplier',
    labels: { singular: 'Fournisseur', plural: 'Fournisseurs' },
    field: 'supplierOptions',
    preload: true,
});

export const categorySource = defineEntitySource({
    key: 'category',
    labels: { singular: 'Catégorie', plural: 'Catégories' },
    field: 'categoryOptions',
    preload: true,
});

export const tagSource = defineEntitySource({
    key: 'tag',
    labels: { singular: 'Tag', plural: 'Tags' },
    field: 'tagOptions',
    preload: true,
});

export const badgeSource = defineEntitySource({
    key: 'badge',
    labels: { singular: 'Badge', plural: 'Badges' },
    field: 'badgeOptions',
    preload: true,
});

export const productArrangementSource = defineEntitySource({
    key: 'productArrangement',
    labels: { singular: 'Gamme', plural: 'Gammes' },
    field: 'productArrangementOptions',
    preload: true,
});

export const microstoreColumnTemplateSource = defineEntitySource({
    key: 'microstoreColumnTemplate',
    labels: { singular: 'Template de colonne', plural: 'Templates de colonne' },
    field: 'microstoreColumnTemplateOptions',
    preload: true,
});

export const productPriceRangeSource = defineEntitySource({
    key: 'productPriceRange',
    labels: { singular: 'Grille tarifaire', plural: 'Grilles tarifaires' },
    field: 'productPriceRangeOptions',
    preload: true,
});

export const equipmentSource = defineEntitySource({
    key: 'equipment',
    labels: { singular: 'Équipement', plural: 'Équipements' },
    field: 'equipmentOptions',
    preload: true,
});

export const deliveryZoneSource = defineEntitySource({
    key: 'deliveryZone',
    labels: { singular: 'Zone de livraison', plural: 'Zones de livraison' },
    field: 'deliveryZoneOptions',
    preload: true,
});

export const nayaxMDBTemplateSource = defineEntitySource({
    key: 'nayaxMDBTemplate',
    labels: { singular: 'Template MDB', plural: 'Templates MDB' },
    field: 'nayaxMDBTemplateOptions',
    preload: true,
});

export const messagesSetSource = defineEntitySource({
    key: 'messagesSet',
    labels: { singular: 'Jeu de messages', plural: 'Jeux de messages' },
    field: 'messagesSetOptions',
    preload: true,
});

/**
 * Variantes de périmètre. Constantes de module : une source se compare par référence dans les
 * dépendances d'effet — la construire au rendu relancerait la recherche sans fin.
 */

/**
 * Les membres de l'équipe, tous états confondus : c'est le sens de « livreur » ou « préparateur ».
 * « Interne » est tranché par le serveur sur les DROITS, jamais par une liste de groupes tenue ici :
 * un utilisateur d'un groupe composé en ferait sinon disparaître.
 */
export const internalUserSource = userSource.withScope({ internal: true, states: null });

/**
 * Toutes les organisations, archivées comprises. Pour FILTRER un historique : une organisation
 * archivée a laissé des livraisons derrière elle, et devenir infiltrable serait une régression.
 */
export const anyStateOrganizationSource = organizationSource.withScope({ states: null });

/**
 * Mêmes variantes « tous états » pour les trois référentiels que filtre l'historique des transactions.
 * Un site fermé, une machine déposée ou un produit sorti du catalogue ont laissé des transactions
 * derrière eux : les retirer des propositions rendrait ces lignes impossibles à isoler.
 */
export const anyStateSiteSource = siteSource.withScope({ states: null, types: null });

export const anyStateMicrostoreColumnSource = microstoreColumnSource.withScope({
    states: null,
    siteIds: null,
});

export const anyStateProductSource = productSource.withScope({
    states: null,
    isMicrostore: null,
    flows: null,
});

// Même raison pour l'acheteur : un compte archivé garde ses transactions et ses paiements.
export const anyStateUserSource = userSource.withScope({ states: null });

/**
 * UNE entrée par collection, pour le test de cohérence avec le schéma — pas les variantes de
 * périmètre. Une variante partage le champ et le type de scope de sa source de base : seul son
 * contenu change, et lui est déjà vérifié par le compilateur via `TScope`.
 */
export const ENTITY_SOURCES = [
    userSource,
    productSource,
    totemSource,
    locationSource,
    microstoreColumnGroupSource,
    subcategorySource,
    columnSource,
    microstoreColumnSource,
    siteSource,
    organizationSource,
    supplierSource,
    categorySource,
    tagSource,
    badgeSource,
    productArrangementSource,
    microstoreColumnTemplateSource,
    productPriceRangeSource,
    equipmentSource,
    deliveryZoneSource,
    nayaxMDBTemplateSource,
    messagesSetSource,
];
